import { useId, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { CircleCheck, Copy, GitMerge, Info, RotateCcw, TriangleAlert, X } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { CONTACT_KIND_LABELS, formatDate, formatDateTime, formatNumber, hasPermission, labelOf, PARTY_SOURCE_LABELS, PARTY_TYPE_LABELS } from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  DescriptionList,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Label,
  PageHeader,
  SearchInput,
  Skeleton,
  Textarea,
  toast,
} from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { DetailSkeleton, ExternalReferences, PartyLink } from '../../components/data.jsx';
import { useListState } from '../../lib/list-search.js';

const STATUS_OPTIONS = [
  { value: 'resolved', label: 'Entschieden' },
  { value: 'all', label: 'Alle' },
];
const TYPE_OPTIONS = [
  { value: 'duplicate_candidate', label: 'Mehrere mögliche Treffer' },
  { value: 'unmatched_owner', label: 'Kein Treffer unter den Eigentümern' },
];
const RESOLUTION_LABELS = { not_duplicate: 'Kein Duplikat', merged: 'Zusammengeführt' };

/** @param {{ issue: any }} props */
function ResolutionBadge({ issue }) {
  if (issue.resolution_status === 'open') return <Badge variant="warning">Offen</Badge>;
  return <Badge variant={issue.resolution === 'merged' ? 'success' : 'secondary'}>{labelOf(RESOLUTION_LABELS, issue.resolution ?? 'resolved')}</Badge>;
}

/**
 * Administration › Dubletten-Prüfung (Migrationsplan §5, `parties.update`): Fälle aus der Migration (Cobra-Adresse ↔
 * WinCasa-Eigentümer), je Quelldatensatz der jüngste Fall. Standard: nur offene.
 */
export function DuplicatesPage() {
  const api = useApi();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const filter = { status: search.status ?? 'open', type: search.type, q: search.q };
  const params = { page: list.apiParams.page, per_page: list.apiParams.per_page, filter };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.duplicates(api, params));
  const columns = useMemo(() => duplicateColumns(), []);
  const open = data?.meta?.open;

  return (
    <>
      <PageHeader
        title="Dubletten-Prüfung"
        description="Personen aus Cobra, die bei der Migration keinem oder mehreren WinCasa-Eigentümern zugeordnet werden konnten. Prüfen, verwerfen oder zusammenführen."
      />
      {open && (
        <p className="mb-3 text-sm text-muted-foreground" aria-live="polite">
          Offen: {formatNumber(open.duplicate_candidate ?? 0)} mit mehreren Treffern · {formatNumber(open.unmatched_owner ?? 0)} ohne Treffer
        </p>
      )}
      <FilterBar>
        <SearchInput label="Fälle durchsuchen" placeholder="Name oder Cobra-ID …" value={search.q ?? ''} onValueChange={(v) => list.setFilter('q', v)} />
        <FilterSelect label="Status" value={search.status} onChange={(v) => list.setFilter('status', v)} options={STATUS_OPTIONS} allLabel="Offen" />
        <FilterSelect label="Art" value={search.type} onChange={(v) => list.setFilter('type', v)} options={TYPE_OPTIONS} />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Dubletten-Fälle"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyState={
            <EmptyState
              compact
              className="border-0"
              icon={Copy}
              title={filter.status === 'open' ? 'Keine offenen Fälle' : 'Keine Fälle'}
              description="Für die gewählten Filter liegen keine Dubletten-Fälle vor."
            />
          }
        />
      )}
    </>
  );
}

function duplicateColumns() {
  return [
    {
      id: 'created_at',
      header: 'Gemeldet',
      enableSorting: false,
      cell: ({ row }) => <span className="numeric whitespace-nowrap">{formatDate(row.original.created_at)}</span>,
    },
    {
      id: 'type',
      header: 'Fall',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span className="font-medium">{row.original.type_label}</span>
          <span className="text-xs text-muted-foreground">Cobra-Adresse {row.original.source.id}</span>
        </span>
      ),
    },
    {
      id: 'source',
      header: 'Person aus Cobra',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.source_party ? <PartyLink party={row.original.source_party} /> : <span className="text-muted-foreground">nicht vorhanden</span>,
    },
    {
      id: 'candidates',
      header: 'Kandidaten',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.candidates.length ? (
          <ul className="flex flex-col gap-0.5">
            {row.original.candidates.map((c) => (
              <li key={c.original_id}>
                {c.party ? <PartyLink party={c.party} /> : <span className="text-muted-foreground">#{c.original_id} (gelöscht)</span>}
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-muted-foreground">–</span>
        ),
    },
    { id: 'status', header: 'Status', enableSorting: false, cell: ({ row }) => <ResolutionBadge issue={row.original} /> },
    {
      id: 'open',
      header: () => <span className="sr-only">Aktion</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <Button asChild size="sm" variant="outline">
          <Link to="/administration/dubletten/$issueId" params={{ issueId: String(row.original.id) }} aria-label={`Fall #${row.original.id} prüfen`}>
            Prüfen
          </Link>
        </Button>
      ),
    },
  ];
}

/** Detail: Gegenüberstellung Quelle ↔ Kandidaten, „Kein Duplikat“ und „Zusammenführen“ (mit Vorschau). */
export function DuplicateDetailPage() {
  const api = useApi();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const { issueId } = useParams({ strict: false });
  const query = useQuery(adminQueries.duplicate(api, issueId));
  const [dismissOpen, setDismissOpen] = useState(false);
  const [mergeCandidate, setMergeCandidate] = useState(/** @type {any} */ (null));
  const canMerge = hasPermission(me, 'parties.delete');

  const breadcrumb = (
    <Link to="/administration/dubletten" className="hover:underline">
      Dubletten-Prüfung
    </Link>
  );
  if (query.error) {
    return (
      <>
        <PageHeader title="Dubletten-Fall" breadcrumb={breadcrumb} />
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      </>
    );
  }
  if (query.isPending) return <DetailSkeleton />;

  const issue = query.data;
  const isOpen = issue.resolution_status === 'open';
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.admin.resource('duplicates') });

  async function reopen() {
    try {
      await api.admin.duplicates.reopen(issue.id);
      await refresh();
      toast.success('Fall wieder geöffnet');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Der Fall konnte nicht wieder geöffnet werden.'));
    }
  }

  return (
    <>
      <PageHeader
        title={`Dubletten-Fall #${issue.id}`}
        description={issue.type_label}
        breadcrumb={breadcrumb}
        actions={
          isOpen ? (
            <Button variant="outline" onClick={() => setDismissOpen(true)}>
              <X aria-hidden="true" /> Kein Duplikat
            </Button>
          ) : (
            issue.resolution === 'not_duplicate' && (
              <Button variant="outline" onClick={reopen}>
                <RotateCcw aria-hidden="true" /> Wieder öffnen
              </Button>
            )
          )
        }
      />
      {!isOpen && <ResolutionAlert issue={issue} />}
      <Card className="mb-3">
        <CardContent className="pt-4">
          <DescriptionList
            items={[
              { label: 'Status', value: <ResolutionBadge issue={issue} /> },
              { label: 'Meldung', value: issue.message },
              { label: 'Quelle', value: `Cobra ADDRESSES · ID ${issue.source.id}` },
              { label: 'Gemeldet', value: `${formatDateTime(issue.created_at)} (Migrationslauf #${issue.migration_run_id}, Schritt ${issue.step})` },
              { label: 'Originalwert', value: issue.original_value ? <code className="text-xs break-all">{issue.original_value}</code> : null },
            ]}
          />
        </CardContent>
      </Card>
      {isOpen && !canMerge && (
        <Alert variant="info" className="mb-3">
          <Info aria-hidden="true" />
          <AlertDescription>Zusammenführen erfordert das Recht „Personen löschen/zusammenführen“ (Administrator). Verwerfen ist möglich.</AlertDescription>
        </Alert>
      )}
      <Comparison issue={issue} onMerge={isOpen && canMerge && issue.source_party_detail ? setMergeCandidate : null} />
      <DismissDialog issue={issue} open={dismissOpen} onOpenChange={setDismissOpen} onDone={refresh} />
      <MergeDialog issue={issue} candidate={mergeCandidate} onClose={() => setMergeCandidate(null)} onDone={refresh} />
    </>
  );
}

function ResolutionAlert({ issue }) {
  const merged = issue.resolution === 'merged';
  return (
    <Alert variant={merged ? 'success' : 'info'} className="mb-3">
      <CircleCheck aria-hidden="true" />
      <div className="flex flex-col gap-1">
        <AlertTitle>{labelOf(RESOLUTION_LABELS, issue.resolution ?? 'resolved')}</AlertTitle>
        <AlertDescription>
          Entschieden {issue.resolved_at ? `am ${formatDateTime(issue.resolved_at)}` : ''}
          {issue.resolved_by ? ` von ${issue.resolved_by.name}` : ''}.{issue.resolution_note ? ` Notiz: ${issue.resolution_note}` : ''}
          {merged && ' Spätere Migrationsläufe ergänzen nur noch die verbleibende Person.'}
        </AlertDescription>
      </div>
    </Alert>
  );
}

/** Side-by-side comparison; values that differ from the Cobra person are highlighted. */
function Comparison({ issue, onMerge }) {
  const source = issue.source_party_detail;
  const candidates = issue.candidate_details ?? [];
  const columns = [
    { key: 'source', title: 'Aus Cobra', subtitle: 'wird zusammengeführt', party: source },
    ...candidates.map((c) => ({
      key: `c-${c.original_id}`,
      title: 'Kandidat',
      subtitle: c.merged_into ? `#${c.original_id} wurde in #${c.merged_into} zusammengeführt` : 'bleibt erhalten',
      party: c.party,
      candidate: c,
    })),
  ];
  const rows = COMPARISON_ROWS;

  if (!source && candidates.length === 0) {
    return <EmptyState icon={Copy} title="Keine Personen" description="Weder die Cobra-Person noch Kandidaten sind (noch) vorhanden." />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Gegenüberstellung</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem] border-collapse text-sm">
            <caption className="sr-only">Vergleich der Cobra-Person mit den Kandidaten</caption>
            <thead>
              <tr className="border-b align-bottom">
                <th scope="col" className="w-40 px-2 py-2 text-left text-xs font-medium text-muted-foreground">
                  Merkmal
                </th>
                {columns.map((col) => (
                  <th key={col.key} scope="col" className="px-2 py-2 text-left">
                    <span className="block text-xs font-medium text-muted-foreground">
                      {col.title} · {col.subtitle}
                    </span>
                    {col.party ? <PartyLink party={col.party} /> : <span className="text-muted-foreground">nicht vorhanden</span>}
                    {onMerge && col.candidate?.party && (
                      <Button size="sm" className="mt-2 flex" onClick={() => onMerge(col.candidate)}>
                        <GitMerge aria-hidden="true" /> Hiermit zusammenführen
                      </Button>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const sourceValue = source ? row.text(source) : '';
                return (
                  <tr key={row.label} className="border-b align-top last:border-0">
                    <th scope="row" className="px-2 py-2 text-left text-xs font-medium text-muted-foreground">
                      {row.label}
                    </th>
                    {columns.map((col) => {
                      const differs = col.key !== 'source' && col.party && source && row.compare && row.text(col.party) !== sourceValue;
                      return (
                        <td key={col.key} className={differs ? 'bg-warning-subtle/60 px-2 py-2' : 'px-2 py-2'}>
                          {col.party ? row.render(col.party) : '–'}
                          {differs && <span className="sr-only"> (abweichend)</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Farbig hinterlegt: Wert weicht von der Cobra-Person ab.</p>
      </CardContent>
    </Card>
  );
}

const text = (v) => (v === null || v === undefined || v === '' ? '' : String(v));
const plain = (key, label) => ({ label, compare: true, text: (p) => text(p[key]), render: (p) => text(p[key]) || '–' });
const contactsText = (p) =>
  (p.contacts ?? [])
    .map((c) => `${labelOf(CONTACT_KIND_LABELS, c.kind)}: ${c.value}`)
    .sort()
    .join('\n');
const addressText = (a) => [[a.street, a.house_number].filter(Boolean).join(' '), [a.postal_code, a.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');

const COMPARISON_ROWS = [
  { label: 'Art', compare: true, text: (p) => text(p.type), render: (p) => labelOf(PARTY_TYPE_LABELS, p.type) },
  plain('salutation', 'Anrede'),
  plain('title', 'Titel'),
  plain('first_name', 'Vorname'),
  plain('last_name', 'Nachname'),
  plain('organization_name', 'Organisation'),
  plain('letter_salutation', 'Briefanrede'),
  { label: 'Geburtstag', compare: true, text: (p) => text(p.birthday), render: (p) => (p.birthday ? formatDate(p.birthday) : '–') },
  { label: 'Quelle', compare: false, text: (p) => text(p.source), render: (p) => labelOf(PARTY_SOURCE_LABELS, p.source) },
  {
    label: 'Kontaktwege',
    compare: true,
    text: contactsText,
    render: (p) =>
      p.contacts?.length ? (
        <ul className="flex flex-col gap-0.5">
          {p.contacts.map((c, i) => (
            <li key={i}>
              <span className="text-muted-foreground">{labelOf(CONTACT_KIND_LABELS, c.kind)}:</span> {c.value}
            </li>
          ))}
        </ul>
      ) : (
        '–'
      ),
  },
  {
    label: 'Adressen',
    compare: true,
    text: (p) => (p.addresses ?? []).map(addressText).sort().join('\n'),
    render: (p) =>
      p.addresses?.length ? (
        <ul className="flex flex-col gap-0.5">
          {p.addresses.map((a, i) => (
            <li key={i}>{addressText(a) || '–'}</li>
          ))}
        </ul>
      ) : (
        '–'
      ),
  },
  { label: 'Externe Schlüssel', compare: false, text: () => '', render: (p) => <ExternalReferences references={p.external_references} /> },
  {
    label: 'Benutzerkonto',
    compare: false,
    text: () => '',
    render: (p) =>
      p.user ? (
        <Link to="/administration/benutzer/$userId" params={{ userId: String(p.user.id) }} className="text-primary hover:underline">
          {p.user.name}
        </Link>
      ) : (
        '–'
      ),
  },
  {
    label: 'Eigentum',
    compare: false,
    text: () => '',
    render: (p) =>
      p.ownerships?.length ? (
        <ul className="flex flex-col gap-0.5">
          {p.ownerships.map((o, i) => (
            <li key={i}>
              <Link to="/einheiten/$unitId" params={{ unitId: String(o.unit_id) }} className="text-primary hover:underline">
                {o.property_name} · Einheit {o.unit_number}
              </Link>
              {!o.current && <span className="ml-1 text-xs text-muted-foreground">(beendet)</span>}
            </li>
          ))}
        </ul>
      ) : (
        '–'
      ),
  },
  {
    label: 'Weitere Verwendung',
    compare: false,
    text: () => '',
    render: (p) =>
      [
        p.usage?.tenancies ? `${p.usage.tenancies} Mietverhältnis(se)` : null,
        p.usage?.property_roles ? `${p.usage.property_roles} Objektrolle(n)` : null,
        p.usage?.meters ? `${p.usage.meters} Zähler (Ableser)` : null,
        p.usage?.client_units ? `${p.usage.client_units} Mandanten-Einheit(en)` : null,
      ]
        .filter(Boolean)
        .join(', ') || '–',
  },
];

function DismissDialog({ issue, open, onOpenChange, onDone }) {
  const api = useApi();
  const [note, setNote] = useState('');
  const [pending, setPending] = useState(false);
  const noteId = useId();
  async function submit(e) {
    e.preventDefault();
    setPending(true);
    try {
      await api.admin.duplicates.dismiss(issue.id, note.trim() || undefined);
      await onDone();
      toast.success('Als „kein Duplikat“ markiert');
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Der Fall konnte nicht abgeschlossen werden.'));
    } finally {
      setPending(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Kein Duplikat?</DialogTitle>
            <DialogDescription>Die Personen bleiben getrennt. Der Fall wird auch bei künftigen Migrationsläufen nicht erneut gemeldet.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={noteId}>Notiz (optional)</Label>
            <Textarea id={noteId} rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Vater und Sohn" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" loading={pending}>
              Kein Duplikat
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MergeDialog({ issue, candidate, onClose, onDone }) {
  const api = useApi();
  const partyId = candidate?.party?.id;
  const preview = useQuery(adminQueries.duplicatePreview(api, issue.id, partyId));
  const [note, setNote] = useState('');
  const [pending, setPending] = useState(false);
  const noteId = useId();
  const plan = preview.data;
  const blocked = Boolean(plan?.blockers?.length);

  async function submit(e) {
    e.preventDefault();
    setPending(true);
    try {
      await api.admin.duplicates.merge(issue.id, partyId, note.trim() || undefined);
      await onDone();
      toast.success('Personen zusammengeführt');
      setNote('');
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Die Personen konnten nicht zusammengeführt werden.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={Boolean(candidate)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Personen zusammenführen?</DialogTitle>
            <DialogDescription>
              „{issue.source_party?.display_name}“ (Cobra) wird in „{candidate?.party?.display_name}“ übernommen und danach gelöscht. Das kann nicht rückgängig
              gemacht werden.
            </DialogDescription>
          </DialogHeader>
          {preview.error ? (
            <ErrorState compact error={preview.error} onRetry={() => preview.refetch()} />
          ) : !plan ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div className="flex flex-col gap-3 text-sm">
              {plan.blockers.length > 0 && (
                <Alert variant="destructive">
                  <TriangleAlert aria-hidden="true" />
                  <AlertDescription>{plan.blockers.join(' ')}</AlertDescription>
                </Alert>
              )}
              {plan.warnings.length > 0 && (
                <Alert variant="warning">
                  <TriangleAlert aria-hidden="true" />
                  <AlertDescription>
                    <ul className="list-disc pl-4">
                      {plan.warnings.map((w) => (
                        <li key={w}>{w}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
              <div>
                <h3 className="mb-1 font-semibold">Wird übernommen</h3>
                {plan.references.length === 0 ? (
                  <p className="text-muted-foreground">Keine Verweise.</p>
                ) : (
                  <ul className="flex flex-col gap-0.5" aria-label="Übernommene Verweise">
                    {plan.references.map((r) => (
                      <li key={r.key} className="flex justify-between gap-3">
                        <span>{r.label}</span>
                        <span className="numeric text-muted-foreground">
                          {formatNumber(r.move)} übernommen{r.drop ? ` · ${formatNumber(r.drop)} doppelt (entfällt)` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={noteId}>Notiz (optional)</Label>
            <Textarea id={noteId} rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" variant="destructive" loading={pending} disabled={!plan || blocked}>
              <GitMerge aria-hidden="true" /> Zusammenführen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
